// frontend/src/components/TransnetyxManagement.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import { ConfirmDialog } from './ConfirmDialog';
import { DataService } from '../services/DataService';

const TransnetyxManagement = () => {
    const toast = useToast();
    const { confirm, confirmDialogProps } = useConfirm();
    const [results, setResults] = useState([]);
    const [mice, setMice] = useState([]);
    const [litters, setLitters] = useState([]);
    const [loading, setLoading] = useState(false);
    // `error` is kept for the three load failures only. Everything else moved
    // to toasts — and for the batch flow that is a fix, not a preference: the
    // banner renders at the top of the page, underneath the batch modal
    // overlay, so those messages were being set where nobody could read them.
    const [error, setError] = useState('');

    // State to hold the selected entity for each result card (keyed by result.id)
    const [selectedEntity, setSelectedEntity] = useState({});
    // State to hold the search term for each result card (keyed by result.id)
    const [searchTerms, setSearchTerms] = useState({});

    const [orderId, setOrderId] = useState('');
    // State to manage which genotype source to use for assignment (Translated or Raw)
    const [assignmentModes, setAssignmentModes] = useState({});
    // State to manage the visibility of the custom search dropdowns
    const [dropdownOpen, setDropdownOpen] = useState({});
    // Ref to hold references to the search container divs to detect outside clicks
    const searchContainerRefs = useRef({});

    // Batch assign state
    const [selectedResults, setSelectedResults] = useState(new Set());
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [batchLitterId, setBatchLitterId] = useState('');
    const [batchMode, setBatchMode] = useState('auto_match'); // 'auto_match' or 'grid_order'
    const [batchPreview, setBatchPreview] = useState([]);
    const [batchProcessing, setBatchProcessing] = useState(false);
    const [batchConflicts, setBatchConflicts] = useState([]);

    // Which result (if any) the single-assign modal is open for.
    const [singleAssignResultId, setSingleAssignResultId] = useState(null);
    // Anchor for shift+click range selection (the last plain-clicked row's id).
    const [rangeAnchorId, setRangeAnchorId] = useState(null);

    useEffect(() => {
        fetchResults();
        fetchMice();
        fetchLitters();
    }, []);

    // When results are loaded, set the default assignment mode for each result.
    useEffect(() => {
        if (results.length > 0) {
            const defaultModes = {};
            results.forEach(result => {
                // Default to 'Translated' if it exists, otherwise 'Raw'
                if (!assignmentModes[result.id]) { // Only set if not already set
                    defaultModes[result.id] = result.translated_result ? 'Translated' : 'Raw';
                }
            });
            if (Object.keys(defaultModes).length > 0) {
                setAssignmentModes(prevModes => ({...prevModes, ...defaultModes}));
            }
        }
    }, [results, assignmentModes]);

    // Effect to handle clicks outside of the search dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            let isClickInside = false;
            Object.keys(dropdownOpen).forEach(resultId => {
                if (dropdownOpen[resultId] && searchContainerRefs.current[resultId] && searchContainerRefs.current[resultId].contains(event.target)) {
                    isClickInside = true;
                }
            });

            if (!isClickInside) {
                setDropdownOpen({}); // Close all dropdowns
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownOpen]);

    const fetchResults = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/transnetyx/results`);
            // Ensure raw_results is parsed from JSON string if needed
            const parsedResults = res.data.map(result => ({
                ...result,
                raw_results: typeof result.raw_results === 'string' ? JSON.parse(result.raw_results) : result.raw_results
            }));
            setResults(parsedResults);
        } catch (err) {
            setError('Failed to fetch Transnetyx results.');
        } finally {
            setLoading(false);
        }
    };

    const fetchMice = async () => {
        try {
            const result = await DataService.getMice({ status: 'Active', limit: 999999 });
            setMice(result.data);
        } catch (err) {
            setError('Failed to fetch mice.');
        }
    };

    const fetchLitters = async () => {
        try {
            const result = await DataService.getLitters({ status: 'Active', limit: 999999 });
            setLitters(result.data);
        } catch (err) {
            setError('Failed to fetch litters.');
        }
    };

    /**
     * Parses a translated result string from Transnetyx into locus/allele pairs.
     * @param {string} translatedString - e.g., "Thy1-GC6s(Tg);Cdh23(Ahl/ahl)"
     * @returns {Array<{locus_name: string, allele_name: string}>}
     */
    const parseTranslatedResult = (translatedString) => {
        if (!translatedString) return [];
        // Split by semicolon for multiple genotypes
        const pairs = translatedString.split(';').map(s => s.trim());
        const genotypeData = [];
        // Regex to capture locus (group 1) and allele (group 2) from "Locus(Allele)" format
        const regex = /(.+?)\((.+?)\)/;

        for (const pair of pairs) {
            const match = pair.match(regex);
            if (match && match[1] && match[2]) {
                genotypeData.push({
                    locus_name: match[1].trim(),
                    allele_name: match[2].trim()
                });
            }
        }
        return genotypeData;
    };

    const handleAssign = async (resultId, result) => {
        const entity = selectedEntity[resultId];
        if (!entity) {
            toast.warn('Please select a mouse or pup to assign.');
            return;
        }

        let genotype_data = [];
        const mode = assignmentModes[resultId];

        if (mode === 'Translated' && result.translated_result) {
            genotype_data = parseTranslatedResult(result.translated_result);
        } else {
            // Fallback to raw results if translated is not available or if Raw is selected
            genotype_data = Array.isArray(result.raw_results) ? result.raw_results.map(r => ({
                locus_name: r.probeName,
                allele_name: r.probeResult
            })) : [];
        }

        if (genotype_data.length === 0) {
            toast.warn('Could not parse any genotype data to assign.');
            return;
        }

        try {
            if (entity.type === 'mouse') {
                await axios.post(`${process.env.REACT_APP_API_URL}/api/transnetyx/assign`, {
                    result_id: resultId,
                    mouse_id: entity.id,
                    genotype_data: genotype_data
                });
            } else if (entity.type === 'pup') {
                await axios.post(`${process.env.REACT_APP_API_URL}/api/transnetyx/assign-pup`, {
                    result_id: resultId,
                    litter_id: entity.litterId,
                    pup_temp_id: entity.pupId,
                    genotype_data: genotype_data
                });
            }

            toast.success('Result assigned successfully!');
            // Optimistically remove the assigned result from the UI
            setResults(prevResults => prevResults.filter(r => r.id !== resultId));
            setSingleAssignResultId(null);

        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to assign result.');
        }
    };

    const handleDelete = async (resultId) => {
        if (await confirm('Are you sure you want to delete this result? This cannot be undone.', {
            confirmLabel: 'Delete',
            danger: true
        })) {
            try {
                await axios.delete(`${process.env.REACT_APP_API_URL}/api/transnetyx/results/${resultId}`);
                toast.success('Result deleted successfully.');
                fetchResults();
            } catch (err) {
                toast.error('Failed to delete result.');
            }
        }
    };

    const handleFetchOrder = async () => {
        if (!orderId) {
            toast.warn("Please enter an Order ID.");
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/transnetyx/order_specific?orderId=${orderId}`);

            // The backend now handles saving. After a successful fetch, we just need to refresh our list of unassigned results.
            toast.success(res.data.message || `Order ${orderId} fetched successfully.`);
            fetchResults(); // This will pull the newly added results from our DB
            setOrderId(''); // Clear the input

        } catch (err) {
            toast.error(`Failed to fetch order ${orderId}.`);
        } finally {
            setLoading(false);
        }
    };

    const formatDatabaseDateLiteral = (dateString) => {
        if (!dateString) return 'N/A';
        const dateObj = new Date(dateString);
        if (isNaN(dateObj.getTime())) return 'Invalid Date';
        const year = dateObj.getUTCFullYear();
        const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getUTCDate()).padStart(2, '0');
        return `${month}/${day}/${year}`;
    };

    // ──────────────────────────────────────────────────────────────────────
    // Batch Assign Logic
    // ──────────────────────────────────────────────────────────────────────

    const toggleResultSelection = useCallback((resultId) => {
        setSelectedResults(prev => {
            const next = new Set(prev);
            if (next.has(resultId)) {
                next.delete(resultId);
            } else {
                next.add(resultId);
            }
            return next;
        });
    }, []);

    // Plain click toggles a row; shift+click selects every row between the
    // anchor and the clicked row (additive, never deselects). Range is scoped
    // to the same wellplate, since that's the visual unit the user sees.
    const handleRowClick = (e, resultId, group) => {
        if (e.shiftKey && rangeAnchorId !== null) {
            const anchorIdx = group.results.findIndex(r => r.id === rangeAnchorId);
            const currentIdx = group.results.findIndex(r => r.id === resultId);
            if (anchorIdx !== -1 && currentIdx !== -1) {
                const [from, to] = anchorIdx <= currentIdx
                    ? [anchorIdx, currentIdx]
                    : [currentIdx, anchorIdx];
                const rangeIds = group.results.slice(from, to + 1).map(r => r.id);
                setSelectedResults(prev => {
                    const next = new Set(prev);
                    rangeIds.forEach(id => next.add(id));
                    return next;
                });
                return;
            }
        }
        toggleResultSelection(resultId);
        setRangeAnchorId(resultId);
    };

    const toggleSelectAll = () => {
        if (selectedResults.size === results.length) {
            setSelectedResults(new Set());
        } else {
            setSelectedResults(new Set(results.map(r => r.id)));
        }
    };

    // Get ungenotyped pups from a litter (pups with at least one empty allele)
    const getUngenotypedPups = useCallback((litterId) => {
        const litter = litters.find(l => l.id === parseInt(litterId));
        if (!litter || !litter.pup_records) return [];

        return litter.pup_records
            .filter(pup => !pup.weaned)
            .filter(pup => {
                // Ungenotyped = has genotype_data with at least one empty allele
                if (!pup.genotype_data || pup.genotype_data.length === 0) return false;
                return pup.genotype_data.some(g => !g.allele_name || g.allele_name.trim() === '');
            })
            .map(pup => ({
                ...pup,
                litterId: litter.id,
                displayTag: pup.physical_tag || `Pup #${pup.id}`,
                litterNumber: litter.litter_number
            }));
    }, [litters]);

    // Parse wellplate location to sortable value (e.g., "A1" → {row: 0, col: 0})
    const parseWellplateLocation = (location) => {
        if (!location) return { row: 999, col: 999, original: '' };
        const match = location.match(/^([A-H])(\d+)$/i);
        if (!match) return { row: 999, col: 999, original: location };
        const row = match[1].toUpperCase().charCodeAt(0) - 65; // A=0, H=7
        const col = parseInt(match[2], 10) - 1; // 1-indexed to 0-indexed
        return { row, col, original: location };
    };

    // Compute batch preview based on selected results, litter, and mode
    const computeBatchPreview = useCallback(() => {
        if (!batchLitterId || selectedResults.size === 0) {
            setBatchPreview([]);
            return;
        }

        const selectedResultsList = results
            .filter(r => selectedResults.has(r.id))
            .sort((a, b) => {
                if (batchMode === 'grid_order') {
                    const aParsed = parseWellplateLocation(a.wellplate_location);
                    const bParsed = parseWellplateLocation(b.wellplate_location);
                    if (aParsed.row !== bParsed.row) return aParsed.row - bParsed.row;
                    return aParsed.col - bParsed.col;
                }
                // auto_match: sort by sample_name for consistency
                return (a.sample_name || '').localeCompare(b.sample_name || '');
            });

        const ungenotypedPups = getUngenotypedPups(batchLitterId);
        const preview = [];

        if (batchMode === 'auto_match') {
            // Two passes so an exact match always wins over a partial one, even when
            // an earlier result in the list would have partial-matched the same pup.
            const usedPupIds = new Set();
            const matched = new Map(); // resultId → pup

            const normalize = (s) => (s || '').toLowerCase().trim();

            // Pass 1: exact match (case/whitespace-insensitive).
            selectedResultsList.forEach(result => {
                const sample = normalize(result.sample_name);
                if (!sample) return;
                const pup = ungenotypedPups.find(p =>
                    !usedPupIds.has(p.id) && normalize(p.physical_tag) === sample
                );
                if (pup) {
                    usedPupIds.add(pup.id);
                    matched.set(result.id, pup);
                }
            });

            // Pass 2: pup tag must appear inside the sample name (e.g. "Cage14_M1" → "M1").
            // Prefer the longest tag to avoid "M12" sample being claimed by "M1" pup.
            selectedResultsList.forEach(result => {
                if (matched.has(result.id)) return;
                const sample = normalize(result.sample_name);
                if (!sample) return;
                let best = null;
                let bestLen = 0;
                for (const pup of ungenotypedPups) {
                    if (usedPupIds.has(pup.id)) continue;
                    const tag = normalize(pup.physical_tag);
                    if (!tag) continue;
                    if (sample.includes(tag) && tag.length > bestLen) {
                        bestLen = tag.length;
                        best = pup;
                    }
                }
                if (best) {
                    usedPupIds.add(best.id);
                    matched.set(result.id, best);
                }
            });

            selectedResultsList.forEach(result => {
                const pup = matched.get(result.id);
                if (pup) {
                    preview.push({
                        resultId: result.id,
                        sampleName: result.sample_name,
                        wellplateLocation: result.wellplate_location,
                        matchedPupId: pup.id,
                        matchedPupTag: pup.displayTag,
                        litterNumber: pup.litterNumber,
                        matched: true
                    });
                } else {
                    preview.push({
                        resultId: result.id,
                        sampleName: result.sample_name,
                        wellplateLocation: result.wellplate_location,
                        matched: false
                    });
                }
            });
        } else {
            // grid_order: assign in wellplate order to ungenotyped pups in order
            selectedResultsList.forEach((result, idx) => {
                if (idx < ungenotypedPups.length) {
                    const pup = ungenotypedPups[idx];
                    preview.push({
                        resultId: result.id,
                        sampleName: result.sample_name,
                        wellplateLocation: result.wellplate_location,
                        matchedPupId: pup.id,
                        matchedPupTag: pup.displayTag,
                        litterNumber: pup.litterNumber,
                        matched: true
                    });
                } else {
                    preview.push({
                        resultId: result.id,
                        sampleName: result.sample_name,
                        wellplateLocation: result.wellplate_location,
                        matched: false
                    });
                }
            });
        }

        setBatchPreview(preview);
        // Any change to selection/litter/mode invalidates conflicts from a previous attempt.
        setBatchConflicts([]);
    }, [selectedResults, batchLitterId, batchMode, results, getUngenotypedPups]);

    // Recompute preview when relevant state changes
    useEffect(() => {
        computeBatchPreview();
    }, [computeBatchPreview]);

    const handleBatchAssign = async () => {
        if (batchPreview.length === 0 || !batchLitterId) {
            toast.warn('Please select a litter and ensure there are matches.');
            return;
        }

        const matchedAssignments = batchPreview.filter(m => m.matched);
        if (matchedAssignments.length === 0) {
            toast.warn('No matched assignments to process.');
            return;
        }

        setBatchProcessing(true);
        setBatchConflicts([]);

        try {
            // Genotype source mode is per-result; use the first one for the batch.
            const firstResultId = matchedAssignments[0].resultId;
            const genotypeSource = assignmentModes[firstResultId] || 'Translated';

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/transnetyx/batch-assign`,
                {
                    assignments: matchedAssignments.map(m => ({
                        result_id: m.resultId,
                        pup_id: m.matchedPupId
                    })),
                    litter_id: parseInt(batchLitterId),
                    genotype_source: genotypeSource
                }
            );

            toast.success(response.data.message || `Successfully assigned ${matchedAssignments.length} genotypes!`);
            setShowBatchModal(false);
            setSelectedResults(new Set());
            setBatchLitterId('');
            setBatchPreview([]);
            fetchResults();
            fetchLitters();

        } catch (err) {
            const data = err.response?.data;
            if (Array.isArray(data?.conflicts) && data.conflicts.length > 0) {
                const previewById = new Map(batchPreview.map(p => [p.resultId, p]));
                setBatchConflicts(data.conflicts.map(c => {
                    const p = previewById.get(c.result_id);
                    return {
                        sample: p?.sampleName || `Result #${c.result_id}`,
                        wellplateLocation: p?.wellplateLocation || '',
                        pupTag: p?.matchedPupTag || `Pup #${c.pup_id}`,
                        reason: c.reason
                    };
                }));
                // The conflict rows below stay in the modal; the headline is a
                // toast so it is visible above the overlay.
                toast.error(data.error || 'Some pairs could not be assigned. See details below.');
            } else {
                toast.error(data?.error || 'Failed to batch assign genotypes.');
            }
        } finally {
            setBatchProcessing(false);
        }
    };

    const openBatchModal = () => {
        setBatchPreview([]);
        setBatchLitterId('');
        setBatchMode('auto_match');
        setBatchConflicts([]);
        setShowBatchModal(true);
    };

    const closeBatchModal = () => {
        setShowBatchModal(false);
        setBatchLitterId('');
        setBatchMode('auto_match');
        setBatchPreview([]);
        setBatchConflicts([]);
    };

    // ──────────────────────────────────────────────────────────────────────
    // Render
    // ──────────────────────────────────────────────────────────────────────

    // Group results by wellplate so the user can select a whole plate at once.
    // "(no wellplate)" buckets anything missing wellplate_number.
    const resultsByWellplate = useMemo(() => {
        const groups = new Map();
        const parseLoc = (loc) => {
            const m = (loc || '').match(/^([A-H])(\d+)$/i);
            if (!m) return { row: 999, col: 999 };
            return { row: m[1].toUpperCase().charCodeAt(0) - 65, col: parseInt(m[2], 10) - 1 };
        };
        for (const r of results) {
            const key = r.wellplate_number || '(no wellplate)';
            if (!groups.has(key)) {
                groups.set(key, { wellplateNumber: key, orderId: r.order_id, results: [] });
            }
            groups.get(key).results.push(r);
        }
        // Sort rows within each plate by grid position (A1 → H12) for predictability.
        for (const g of groups.values()) {
            g.results.sort((a, b) => {
                const pa = parseLoc(a.wellplate_location);
                const pb = parseLoc(b.wellplate_location);
                if (pa.row !== pb.row) return pa.row - pb.row;
                return pa.col - pb.col;
            });
        }
        return Array.from(groups.values());
    }, [results]);

    const groupSelectionState = (group) => {
        const ids = group.results.map(r => r.id);
        const selectedCount = ids.filter(id => selectedResults.has(id)).length;
        if (selectedCount === 0) return 'none';
        if (selectedCount === ids.length) return 'all';
        return 'some';
    };

    const toggleGroupSelection = (group) => {
        const state = groupSelectionState(group);
        setSelectedResults(prev => {
            const next = new Set(prev);
            if (state === 'all') {
                group.results.forEach(r => next.delete(r.id));
            } else {
                group.results.forEach(r => next.add(r.id));
            }
            return next;
        });
    };

    // HTML doesn't expose `indeterminate` as a prop; set it via a ref callback.
    const setIndeterminate = (node, value) => {
        if (node) node.indeterminate = value;
    };

    const openSingleAssignModal = (resultId) => {
        setSingleAssignResultId(resultId);
        // Default to Translated mode if the user hasn't picked one yet.
        if (!assignmentModes[resultId]) {
            setAssignmentModes(prev => ({ ...prev, [resultId]: 'Translated' }));
        }
    };
    const closeSingleAssignModal = () => setSingleAssignResultId(null);

    const formatRawResults = (raw) =>
        Array.isArray(raw) ? raw.map(r => `${r.probeName}(${r.probeResult})`).join(', ') : 'N/A';

    const assignableEntities = useMemo(() => {
        const mouseEntities = mice.map(mouse => ({
            type: 'mouse',
            id: mouse.id,
            display: `Mouse: ${mouse.physical_tag || ''} (Cage: ${mouse.display_cage_number || 'N/A'})`,
            searchKey: (mouse.physical_tag || '').toLowerCase(),
            ...mouse
        }));

        const pupEntities = litters.flatMap(litter =>
            (litter.pup_records || [])
            .filter(pup => !pup.weaned)
            .map((pup, index) => ({
                type: 'pup',
                id: `litter-${litter.id}-pup-${pup.id}`, // Unique key for React
                litterId: litter.id,
                pupId: pup.id,
                display: `Pup: ${pup.physical_tag || `Pup #${index + 1}`} (Cage: ${litter.display_cage_number || 'N/A'}, DOB: ${formatDatabaseDateLiteral(litter.date_of_birth)})`,
                searchKey: `${pup.physical_tag || `pup ${index + 1}`} ${litter.display_cage_number}`.toLowerCase(),
                ...pup
            }))
        );

        return [...mouseEntities, ...pupEntities];
    }, [mice, litters]);

    // Filter mice and pups based on the search term for a specific result card
    const getFilteredEntities = (resultId) => {
        const searchTerm = (searchTerms[resultId] || '').toLowerCase();
        if (!searchTerm) {
            return assignableEntities;
        }
        return assignableEntities.filter(entity =>
            entity.searchKey.includes(searchTerm)
        );
    };

    // Get litters with ungenotyped pups for the batch modal dropdown
    const littersWithUngenotypedPups = useMemo(() => {
        return litters.filter(l => getUngenotypedPups(l.id).length > 0);
    }, [litters, getUngenotypedPups]);

    return (
        <div>
            {confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}
            <h3>Unassigned Transnetyx Genotyping Results</h3>
            {error && <div className="error">{error}</div>}

            <div className="form-group" style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                    type="text"
                    placeholder="Fetch by Wellplate ID"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    style={{flex: 1}}
                />
                <button onClick={handleFetchOrder} disabled={loading} className="primary-button" style={{width: 'auto'}}>
                    {loading ? 'Fetching...' : 'Fetch Order'}
                </button>
            </div>

            {/* Batch Assign Toolbar — always visible so the batch flow reads as the default action. */}
            <div style={{
                marginBottom: '1rem',
                padding: '0.75rem 1rem',
                backgroundColor: selectedResults.size > 0 ? '#e7f3ff' : '#f1f3f5',
                border: `1px solid ${selectedResults.size > 0 ? '#b6d4fe' : '#dee2e6'}`,
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem'
            }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                    <span style={{ fontWeight: 'bold', color: selectedResults.size > 0 ? '#084298' : '#6c757d' }}>
                        {selectedResults.size > 0
                            ? `${selectedResults.size} result${selectedResults.size > 1 ? 's' : ''} selected`
                            : 'Select results or a wellplate to batch-assign'}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                        Click a row to select · shift+click to select a range · use the Assign… button for individual assignment
                    </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        onClick={openBatchModal}
                        className="primary-button"
                        disabled={selectedResults.size === 0}
                        style={{ width: 'auto', opacity: selectedResults.size === 0 ? 0.6 : 1 }}
                    >
                        Batch Assign{selectedResults.size > 0 ? ` (${selectedResults.size})` : ''}
                    </button>
                    {selectedResults.size > 0 && (
                        <button
                            onClick={() => setSelectedResults(new Set())}
                            className="secondary-button"
                            style={{ width: 'auto' }}
                        >
                            Clear Selection
                        </button>
                    )}
                </div>
            </div>

            {loading && results.length === 0 && <p>Loading results...</p>}

            <div className="rules-grid">
                {resultsByWellplate.map(group => {
                    const state = groupSelectionState(group);
                    return (
                        <div key={group.wellplateNumber} className="rule-card">
                            <div className="rule-header" style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                cursor: 'pointer'
                            }} onClick={() => toggleGroupSelection(group)}>
                                <input
                                    type="checkbox"
                                    checked={state === 'all'}
                                    ref={node => setIndeterminate(node, state === 'some')}
                                    onChange={() => toggleGroupSelection(group)}
                                    onClick={e => e.stopPropagation()}
                                    title="Select all results in this wellplate"
                                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                />
                                <div style={{ flex: 1 }}>
                                    <h5 style={{ margin: 0 }}>Wellplate: {group.wellplateNumber}</h5>
                                    <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                                        Order ID: {group.orderId ?? 'N/A'} · {group.results.length} sample{group.results.length === 1 ? '' : 's'}
                                    </span>
                                </div>
                            </div>

                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                marginTop: '0.75rem',
                                fontSize: '0.9rem'
                            }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #dee2e6', color: '#6c757d', fontSize: '0.8rem' }}>
                                        <th style={{ padding: '0.4rem', width: '32px' }}></th>
                                        <th style={{ padding: '0.4rem' }}>Sample</th>
                                        <th style={{ padding: '0.4rem', width: '60px' }}>Well</th>
                                        <th style={{ padding: '0.4rem' }}>Translated</th>
                                        <th style={{ padding: '0.4rem' }}>Raw</th>
                                        <th style={{ padding: '0.4rem', width: '160px', textAlign: 'right' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.results.map(result => (
                                        <tr
                                            key={result.id}
                                            onClick={(e) => handleRowClick(e, result.id, group)}
                                            title="Click to select · shift+click to select a range · use Assign… for individual assignment"
                                            style={{
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #f1f3f5',
                                                backgroundColor: selectedResults.has(result.id) ? '#f0f7ff' : 'transparent',
                                                userSelect: 'none'
                                            }}
                                        >
                                            <td style={{ padding: '0.4rem' }} onClick={e => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedResults.has(result.id)}
                                                    onChange={() => {
                                                        toggleResultSelection(result.id);
                                                        setRangeAnchorId(result.id);
                                                    }}
                                                    title="Select for batch assign"
                                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                />
                                            </td>
                                            <td style={{ padding: '0.4rem', fontWeight: 500 }}>{result.sample_name}</td>
                                            <td style={{ padding: '0.4rem', color: '#495057' }}>{result.wellplate_location || '—'}</td>
                                            <td style={{ padding: '0.4rem', color: '#495057' }}>{result.translated_result || 'N/A'}</td>
                                            <td style={{ padding: '0.4rem', color: '#6c757d', fontSize: '0.85rem' }}>{formatRawResults(result.raw_results)}</td>
                                            <td style={{ padding: '0.4rem', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                                                <button
                                                    className="secondary-button"
                                                    onClick={() => openSingleAssignModal(result.id)}
                                                    style={{ width: 'auto', padding: '0.25rem 0.6rem', fontSize: '0.85rem', marginRight: '0.25rem' }}
                                                >
                                                    Assign…
                                                </button>
                                                <button
                                                    className="secondary-button"
                                                    onClick={() => handleDelete(result.id)}
                                                    style={{ width: 'auto', padding: '0.25rem 0.6rem', fontSize: '0.85rem', backgroundColor: '#dc3545' }}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
                {results.length === 0 && !loading && <p className="no-data">No unassigned results found.</p>}
            </div>

            {/* Batch Assign Modal */}
            {showBatchModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    zIndex: 1000,
                    padding: '2rem',
                    overflowY: 'auto'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        width: '100%',
                        maxWidth: '700px',
                        marginTop: '2rem',
                        marginBottom: '2rem'
                    }}>
                        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Batch Assign Genotypes</h3>

                        {/* Litter Selection */}
                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label style={{ fontWeight: 'bold' }}>Select Litter:</label>
                            <select
                                value={batchLitterId}
                                onChange={(e) => setBatchLitterId(e.target.value)}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                            >
                                <option value="">-- Choose a litter --</option>
                                {littersWithUngenotypedPups.map(litter => {
                                    const ungenotypedCount = getUngenotypedPups(litter.id).length;
                                    return (
                                        <option key={litter.id} value={litter.id}>
                                            {litter.litter_number} (Litter {litter.id}) - {ungenotypedCount} ungenotyped pup(s)
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        {/* Assignment Mode */}
                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label style={{ fontWeight: 'bold' }}>Assignment Mode:</label>
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="batch-mode"
                                        value="auto_match"
                                        checked={batchMode === 'auto_match'}
                                        onChange={() => setBatchMode('auto_match')}
                                    />
                                    Auto-match by tag
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="batch-mode"
                                        value="grid_order"
                                        checked={batchMode === 'grid_order'}
                                        onChange={() => setBatchMode('grid_order')}
                                    />
                                    Grid order (A1→H12)
                                </label>
                            </div>
                            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                                {batchMode === 'auto_match'
                                    ? 'Matches sample names to pup physical tags (partial match allowed).'
                                    : 'Assigns samples in wellplate grid order (A1, A2, ..., H12) to ungenotyped pups in order.'}
                            </p>
                        </div>

                        {/* Preview */}
                        {batchPreview.length > 0 && (
                            <div style={{
                                marginBottom: '1rem',
                                padding: '0.75rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                maxHeight: '300px',
                                overflowY: 'auto'
                            }}>
                                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>Preview ({batchPreview.filter(m => m.matched).length} matched, {batchPreview.filter(m => !m.matched).length} unmatched):</h4>
                                {batchPreview.map((item, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            padding: '0.5rem',
                                            marginBottom: '0.25rem',
                                            borderRadius: '4px',
                                            backgroundColor: item.matched ? '#d4edda' : '#f8d7da',
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        <strong>{item.sampleName}</strong> ({item.wellplateLocation})
                                        {' → '}
                                        {item.matched ? (
                                            <strong>{item.matchedPupTag}</strong>
                                        ) : (
                                            <span style={{ color: '#721c24' }}>No match found</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Conflicts from a previous attempt */}
                        {batchConflicts.length > 0 && (
                            <div style={{
                                marginBottom: '1rem',
                                padding: '0.75rem',
                                backgroundColor: '#f8d7da',
                                border: '1px solid #f5c6cb',
                                borderRadius: '8px'
                            }}>
                                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#721c24' }}>
                                    Could not complete batch — no changes were saved:
                                </h4>
                                {batchConflicts.map((c, idx) => (
                                    <div key={idx} style={{ fontSize: '0.85rem', color: '#721c24', marginBottom: '0.25rem' }}>
                                        • <strong>{c.sample}</strong>
                                        {c.wellplateLocation ? ` (${c.wellplateLocation})` : ''}
                                        {' → '}
                                        <strong>{c.pupTag}</strong>: {c.reason}
                                    </div>
                                ))}
                                <p style={{ fontSize: '0.8rem', color: '#721c24', marginTop: '0.5rem', marginBottom: 0 }}>
                                    Adjust your selection or refresh and try again.
                                </p>
                            </div>
                        )}

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={closeBatchModal}
                                className="secondary-button"
                                disabled={batchProcessing}
                                style={{ width: 'auto' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleBatchAssign}
                                className="primary-button"
                                disabled={batchProcessing || batchPreview.filter(m => m.matched).length === 0}
                                style={{ width: 'auto' }}
                            >
                                {batchProcessing ? 'Assigning...' : `Assign ${batchPreview.filter(m => m.matched).length}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Single Assign Modal */}
            {singleAssignResultId !== null && (() => {
                const result = results.find(r => r.id === singleAssignResultId);
                if (!result) return null;
                return (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        zIndex: 1000,
                        padding: '2rem',
                        overflowY: 'auto'
                    }} onClick={closeSingleAssignModal}>
                        <div
                            onClick={e => e.stopPropagation()}
                            style={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                padding: '1.5rem',
                                width: '100%',
                                maxWidth: '560px',
                                marginTop: '2rem',
                                marginBottom: '2rem'
                            }}
                        >
                            <h3 style={{ marginTop: 0, marginBottom: '0.25rem' }}>Assign Sample: {result.sample_name}</h3>
                            <p style={{ color: '#6c757d', marginTop: 0, marginBottom: '1rem', fontSize: '0.9rem' }}>
                                Wellplate {result.wellplate_number || '—'} · Well {result.wellplate_location || '—'} · Order {result.order_id ?? 'N/A'}
                            </p>

                            <div className="form-group" style={{ marginBottom: '1rem' }}>
                                <label style={{ fontWeight: 'bold' }}>Assign Genotype Using:</label>
                                <div style={{ display: 'flex', gap: '1rem', margin: '0.5rem 0' }}>
                                    {result.translated_result && (
                                        <label>
                                            <input
                                                type="radio"
                                                name={`single-mode-${result.id}`}
                                                value="Translated"
                                                checked={assignmentModes[result.id] === 'Translated'}
                                                onChange={() => setAssignmentModes({ ...assignmentModes, [result.id]: 'Translated' })}
                                            />
                                            Translated
                                        </label>
                                    )}
                                    <label>
                                        <input
                                            type="radio"
                                            name={`single-mode-${result.id}`}
                                            value="Raw"
                                            checked={assignmentModes[result.id] === 'Raw'}
                                            onChange={() => setAssignmentModes({ ...assignmentModes, [result.id]: 'Raw' })}
                                        />
                                        Raw
                                    </label>
                                </div>
                                <p style={{ padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6', margin: 0 }}>
                                    <strong>To Assign:</strong>
                                    {' '}
                                    {assignmentModes[result.id] === 'Translated'
                                        ? result.translated_result
                                        : formatRawResults(result.raw_results)}
                                </p>
                            </div>

                            <div className="form-group" style={{ marginBottom: '1rem' }}>
                                <label style={{ fontWeight: 'bold' }}>Assign to Mouse or Pup:</label>
                                <div
                                    className="custom-search-select"
                                    style={{ position: 'relative' }}
                                    ref={el => (searchContainerRefs.current[result.id] = el)}
                                >
                                    <input
                                        type="text"
                                        placeholder="Search by Physical Tag or Litter Number..."
                                        value={searchTerms[result.id] || ''}
                                        onChange={(e) => {
                                            setSearchTerms({ ...searchTerms, [result.id]: e.target.value });
                                            setSelectedEntity({ ...selectedEntity, [result.id]: null });
                                            setDropdownOpen({ ...dropdownOpen, [result.id]: true });
                                        }}
                                        onFocus={() => setDropdownOpen({ ...dropdownOpen, [result.id]: true })}
                                        style={{ width: '100%' }}
                                    />
                                    {dropdownOpen[result.id] && (
                                        <div className="search-results-dropdown">
                                            {getFilteredEntities(result.id).length > 0 ? (
                                                getFilteredEntities(result.id).map(entity => (
                                                    <div
                                                        key={entity.id}
                                                        className="search-result-item"
                                                        onClick={() => {
                                                            setSelectedEntity({ ...selectedEntity, [result.id]: entity });
                                                            setSearchTerms({ ...searchTerms, [result.id]: entity.display });
                                                            setDropdownOpen({ ...dropdownOpen, [result.id]: false });
                                                        }}
                                                    >
                                                        {entity.display}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="search-result-item-none">No mice or pups found</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={closeSingleAssignModal}
                                    className="secondary-button"
                                    style={{ width: 'auto' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => { handleDelete(result.id); closeSingleAssignModal(); }}
                                    className="secondary-button"
                                    style={{ width: 'auto', backgroundColor: '#dc3545' }}
                                >
                                    Delete
                                </button>
                                <button
                                    className="primary-button"
                                    onClick={() => handleAssign(result.id, result)}
                                    style={{ width: 'auto' }}
                                >
                                    Assign
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Add some basic styling for the custom dropdown */}
            <style>{`
                .search-results-dropdown {
                    position: absolute;
                    width: 100%;
                    background-color: white;
                    border: 1px solid #ccc;
                    border-top: none;
                    border-radius: 0 0 4px 4px;
                    max-height: 200px;
                    overflow-y: auto;
                    z-index: 100;
                }
                .search-result-item, .search-result-item-none {
                    padding: 8px;
                    cursor: pointer;
                }
                .search-result-item:hover {
                    background-color: #f0f0f0;
                }
                .search-result-item-none {
                    color: #666;
                    cursor: default;
                }
            `}</style>
        </div>
    );
};

export default TransnetyxManagement;
